import {Component} from '$src/handy'
import * as xlsx from 'xlsx'
import loadResearchState from '../comparableSales/loadResearchState'
import {loadHousingCorporations} from './HousingCorporationsListPage'
import {getSnapshot} from 'mobx-state-tree'
import {ResearchState, HousingCorporation} from '$src/bootstrap/Store'
import {
  getDataForTabs,
  getDataForTabsRent,
  columns as summaryColumns,
} from '$src/comparableSales/AllTabsSummary'
import {DrawingState} from './TheMap'
import moment from 'moment'

type State = Record<
  string,
  {
    corp: typeof HousingCorporation.Type
    researchState: typeof ResearchState.Type
  }
>
export default async function exportProjectToSpreadsheet(
  env: Component<unknown, unknown>,
  projectId: string,
) {
  await loadHousingCorporations(env, projectId)
  const corps: Record<
    string,
    typeof HousingCorporation.Type
  > = env.store.housingCorporations.list
    .filter(l => !l.hidden)
    .reduce((acc, corp) => {
      acc[corp._id] = corp
      return acc
    }, {})

  await Promise.all(
    Object.keys(corps).map(corpId => {
      return loadResearchState(env, corpId, projectId)
    }),
  )

  const state: State = Object.entries(corps).reduce(
    (acc, [corpId, corp]) => {
      acc[corpId] = {
        corp,
        researchState: env.store.researchStates.byHousingCorporationId.get(
          corpId,
        )!,
      }
      return acc
    },
    {} as State,
  )

  const wb = xlsx.utils.book_new()
  xlsx.utils.book_append_sheet(wb, createSummaries(state), 'Summaries')
  xlsx.utils.book_append_sheet(wb, createFilters(state), 'Filters')
  xlsx.utils.book_append_sheet(
    wb,
    createComparableSalesSheet(state),
    'Comparable Sales',
  )
  xlsx.utils.book_append_sheet(wb, createRentSheet(state), 'Comparable Rents')

  xlsx.writeFile(wb, `export.xlsx`)
}

function createSummaries(state: State) {
  const rows: $FixMe[] = []
  Object.entries(state).forEach(([_, d]) => {
    rows.push(
      ...getDataForTabs(d.researchState.tabs.list, d.corp).map(data => {
        const row: Record<string, unknown> = {
          'Housing corporation': d.corp.name,
        }

        summaryColumns.forEach(col => {
          row[col.Header] = data[col.accessor]
        })

        row['Indexing'] = d.corp.isSummaryAdjusted() ? 'Yes' : 'No'

        return row
      }),
    )

    rows.push(
      ...getDataForTabs(d.researchState.tabs.list, d.corp).map(data => {
        const row: Record<string, unknown> = {
          'Housing corporation': d.corp.name,
        }

        summaryColumns.forEach(col => {
          row[col.Header] = data[col.accessor]
        })

        row['Indexing'] = d.corp.isSummaryAdjusted() ? 'Yes' : 'No'

        return row
      }),
    )

    rows.push(
      ...getDataForTabsRent(d.researchState.tabs.list, d.corp).map(data => {
        const row: Record<string, unknown> = {
          'Housing corporation': d.corp.name,
        }
        summaryColumns.forEach(col => {
          row[col.Header] = data[col.accessor]
        })
        row['Indexing'] = 'No'

        return row
      }),
    )
  })
  const ws = xlsx.utils.json_to_sheet(rows)
  return ws
}

function createFilters(state: State) {
  const rows: $FixMe[] = []
  Object.entries(state).forEach(([_, d]) => {
    d.researchState.tabs.list
      .filter(tab => tab.enabled !== false)
      .forEach(tab => {
        const row: Record<string, unknown> = {
          'Housing corporation': d.corp.name,
          'Tab name': tab.name,
        }

        const values = getSnapshot(tab.query.formValues.values)

        row['Property Type / Row house'] = !Array.isArray(values.propertyType)
          ? true
          : values.propertyType.includes('rowHouse')

        row['Property Type / Detached House'] = !Array.isArray(
          values.propertyType,
        )
          ? true
          : values.propertyType.includes('detachedhouse')

        row['Property Type / Apartment'] = !Array.isArray(values.propertyType)
          ? true
          : values.propertyType.includes('apartment')

        row['Municipality'] = Array.isArray(values.municipality)
          ? values.municipality.join(', ')
          : undefined

        row['Postal Codes'] = Array.isArray(values.postalCodes)
          ? values.postalCodes.join(', ')
          : undefined

        row['Street Address'] =
          typeof values.streetAddress === 'string' &&
          values.streetAddress.trim().length > 0
            ? values.streetAddress
            : undefined

        row['Living Area / From'] =
          values.livingArea && values.livingArea.from
            ? parseInt(values.livingArea.from)
            : undefined

        row['Living Area / To'] =
          values.livingArea && values.livingArea.to
            ? parseInt(values.livingArea.to)
            : undefined

        row['Year of Construction / From'] =
          values.yearOfConstruction && values.yearOfConstruction.from
            ? parseInt(values.yearOfConstruction.from)
            : undefined

        row['Year of Construction / To'] =
          values.yearOfConstruction && values.yearOfConstruction.to
            ? parseInt(values.yearOfConstruction.to)
            : undefined

        row['Number of Rooms / From'] =
          values.numberOfRooms && values.numberOfRooms.from
            ? parseInt(values.numberOfRooms.from)
            : undefined

        row['Number of Rooms / To'] =
          values.numberOfRooms && values.numberOfRooms.to
            ? parseInt(values.numberOfRooms.to)
            : undefined

        row['Newly Built'] =
          values.newlyBuilt === true
            ? 'Yes'
            : values.newlyBuilt === false
            ? 'No'
            : `Doesn't matter`

        row['Condition'] = Array.isArray(values.condition)
          ? values.condition.join(', ')
          : undefined

        row['Length of Sale / From'] =
          values.sellLength && values.sellLength.from
            ? parseInt(values.sellLength.from)
            : undefined

        row['Length of Sale / To'] =
          values.sellLength && values.sellLength.to
            ? parseInt(values.sellLength.to)
            : undefined

        row['Date of Sale / From'] =
          values.dateOfSale && values.dateOfSale.startDate
            ? moment.unix(values.dateOfSale.startDate).format('DD/MM/YYYY')
            : undefined

        row['Date of Sale / To'] =
          values.dateOfSale && values.dateOfSale.endDate
            ? moment.unix(values.dateOfSale.endDate).format('DD/MM/YYYY')
            : undefined

        row['Plot Owned'] =
          values.plotOwned === true
            ? 'Yes'
            : values.plotOwned === false
            ? 'No'
            : `Doesn't matter`

        row['Has Elevator'] =
          values.hasElevator === true
            ? 'Yes'
            : values.hasElevator === false
            ? 'No'
            : `Doesn't matter`

        row['Has Sauna'] =
          values.hasSauna === true
            ? 'Yes'
            : values.hasSauna === false
            ? 'No'
            : `Doesn't matter`

        row['Owner'] = Array.isArray(values.owner)
          ? values.owner.join(', ')
          : undefined

        row['Source'] = Array.isArray(values.source)
          ? values.source.join(', ')
          : undefined

        const mapShape: Exclude<DrawingState, null> = tab.query.mapShape

        row['Map Shape'] = mapShape ? mapShape.type : ''
        row['Map Shape / Circle Lat'] = mapShape
          ? mapShape.type === 'Circle' && mapShape.c.lat
          : ''
        row['Map Shape / Circle Long'] = mapShape
          ? mapShape.type === 'Circle' && mapShape.c.long
          : ''
        row['Map Shape / Circle Radius'] = mapShape
          ? mapShape.type === 'Circle' && mapShape.radius
          : ''
        row['Map Shape / Polygon points'] = mapShape
          ? mapShape.type === 'Polygon' && JSON.stringify(mapShape.points)
          : ''

        rows.push(row)
      })
  })
  const ws = xlsx.utils.json_to_sheet(rows)
  return ws
}

function createComparableSalesSheet(state: State) {
  const rows: $FixMe[] = []
  Object.entries(state).forEach(([_, d]) => {
    d.researchState.tabs.list
      .filter(tab => tab.enabled !== false)
      .forEach(tab => {
        // `selectedIdsOldData` is the older data
        if (tab.foundItems.selectedIdsOldData.size > 0) {
          tab.foundItems.selectedIdsOldData.keys().forEach(foundItemId => {
            const foundItem = tab.foundItems.byId.get(foundItemId)
            if (!foundItem) return
            const row: Record<string, unknown> = {
              'Housing corporation': d.corp.name,
              'Tab name': tab.name,
              ID: foundItem.id,
              'Location / Lat': foundItem.location.lat,
              'Location / Long': foundItem.location.long,
              'Property Type': foundItem.propertyType,
              Municipality: foundItem.municipality,
              'Postal Code': foundItem.postalCode,
              'Living area': foundItem.livingArea,
              'Street address': foundItem.streetAddress,
              'Year of construction': foundItem.yearOfConstruction,
              'Number of rooms': foundItem.numberOfRooms,
              'Newly built': foundItem.newlyBuilt,
              Condition: foundItem.condition,
              'Date of sale': foundItem.dateOfSale,
              'Plot owned': foundItem.plotOwned,
              'Has elevator': foundItem.hasElevator,
              'Has sauna': foundItem.hasSauna,
              'Apartment type exact': foundItem.apartmentTypeExact,
              'Rooms description': foundItem.roomsDescription,
              'Floor number': foundItem.floorNumber,
              'Price sqm adjusted': foundItem.priceSqmAdjusted,
              'Price free of dept adjusted': foundItem.priceFreeOfDeptAdjusted,
              'Price sqm': foundItem.priceSqm,
              'Price free of dept': foundItem.priceFreeOfDept,
            }

            rows.push(row)
          })
        }

        // `selectedIds` is the newer data
        if (tab.foundItems.selectedIds.size > 0) {
          tab.foundItems.selectedIds.keys().forEach(foundItemId => {
            const foundItem = tab.foundItems.byId.get(foundItemId)
            if (!foundItem) return
            const row: Record<string, unknown> = {
              'Housing corporation': d.corp.name,
              'Tab name': tab.name,
              ID: foundItem.id,
              'Location / Lat': foundItem.location.lat,
              'Location / Long': foundItem.location.long,
              'Property Type': foundItem.propertyType,
              Municipality: foundItem.municipality,
              'Postal Code': foundItem.postalCode,
              'Living area': foundItem.livingArea,
              'Street address': foundItem.streetAddress,
              'Year of construction': foundItem.yearOfConstruction,
              'Number of rooms': foundItem.numberOfRooms,
              'Newly built': foundItem.newlyBuilt,
              Condition: foundItem.condition,
              'Date of sale': foundItem.dateOfSale,
              'Plot owned': foundItem.plotOwned,
              'Has elevator': foundItem.hasElevator,
              'Has sauna': foundItem.hasSauna,
              'Apartment type exact': foundItem.apartmentTypeExact,
              'Rooms description': foundItem.roomsDescription,
              'Floor number': foundItem.floorNumber,
              'Price sqm adjusted': foundItem.priceSqmAdjusted,
              'Price free of dept adjusted': foundItem.priceFreeOfDeptAdjusted,
              'Price sqm': foundItem.priceSqm,
              'Price free of dept': foundItem.priceFreeOfDept,
              'Price 1Q ago': foundItem.priceSqmAdjusted1QuarterAgo,
              'Price 2Q ago': foundItem.priceSqmAdjusted2QuartersAgo,
              'Price 3Q ago': foundItem.priceSqmAdjusted3QuartersAgo,
              'Price 4Q ago': foundItem.priceSqmAdjusted4QuartersAgo,
            }
            rows.push(row)
          })
        }
      })
  })
  const ws = xlsx.utils.json_to_sheet(rows)
  return ws
}

function createRentSheet(state: State) {
  const rows: $FixMe[] = []

  Object.entries(state).forEach(([_, d]) => {
    d.researchState.tabs.list
      .filter(tab => tab.enabled !== false)
      .forEach(tab => {
        tab.foundItems.selectedIdsRent.keys().forEach(foundItemId => {
          const foundItem = tab.foundItems.byId.get(foundItemId)
          if (!foundItem) return
          if (foundItem.dataType === 'rent') {
            const row: Record<string, unknown> = {
              'Housing corporation': d.corp.name,
              'Tab name': tab.name,
              ID: foundItem.id,
              'Location / Lat': foundItem.location.lat,
              'Location / Long': foundItem.location.long,
              'Property Type': foundItem.propertyType,
              'Accommodation type': foundItem.accommodationType,
              Municipality: foundItem.municipality,
              'Postal Code': foundItem.postalCode,
              'Living area': foundItem.livingArea,
              'Street address': foundItem.streetAddress,
              'Year of construction': foundItem.yearOfConstruction,
              'Number of rooms': foundItem.numberOfRooms,
              Condition: foundItem.condition,
              'Date of Rent Observation': foundItem.dateOfRent,
              'Has sauna': foundItem.hasSauna,
              'Rooms description': foundItem.roomsDescription,
              'Floor number': foundItem.floorNumber,
              'Rent price': foundItem.rent,
              'Rent price sqm': foundItem.rentSqm,
              Source: foundItem.source,
              Owner: foundItem.owner,
            }
            rows.push(row)
          }
        })
      })
  })
  const ws = xlsx.utils.json_to_sheet(rows)
  return ws
}



// WEBPACK FOOTER //
// ./src/housingCorporations/exportProjectToSpreadsheet.ts